// src/pages/PostDetail/index.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Typography,
  Space,
  Button,
  Tag,
  Divider,
  message,
  Input,
} from "antd";
import dayjs from "dayjs";
import { getPost, removePost, addBookmark } from "../../api/mockData";
import { useAuth } from "../../context/AuthContext";

export default function PostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthed } = useAuth();
  const [post, setPost] = useState(null);
  const [progress, setProgress] = useState(0);
  const [query, setQuery] = useState("");
  const hasRestored = useRef(false);

  useEffect(() => {
    getPost(id).then(setPost);
  }, [id]);

  useEffect(() => {
    if (!post || hasRestored.current) return;
    const params = new URLSearchParams(location.search);
    const pos = Number(params.get("pos"));
    if (Number.isFinite(pos)) {
      hasRestored.current = true;
      requestAnimationFrame(() => {
        window.scrollTo({ top: pos, behavior: "smooth" });
      });
    }
  }, [location.search, post]);

  useEffect(() => {
    const handleScroll = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - doc.clientHeight;
      const next = total > 0 ? doc.scrollTop / total : 0;
      setProgress(Math.min(1, Math.max(0, next)));
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const contentBlocks = useMemo(() => {
    if (!post?.content) return [];
    return post.content.split("\n").map((line, idx) => {
      const match = /^(#{1,3})\s+(.*)$/.exec(line.trim());
      if (!match) return { type: "p", text: line, id: `p-${idx}` };
      const level = match[1].length;
      const raw = match[2].trim();
      const slug = raw.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      return {
        type: "h",
        text: raw,
        level,
        id: slug ? `h-${slug}-${idx}` : `h-${idx}`,
      };
    });
  }, [post]);

  const matchIds = useMemo(() => {
    if (!query) return [];
    const lower = query.toLowerCase();
    return contentBlocks
      .filter((b) => b.text?.toLowerCase().includes(lower))
      .map((b) => b.id);
  }, [contentBlocks, query]);

  const handleLocate = () => {
    if (!query.trim()) return;
    if (!matchIds.length) {
      message.info("未找到匹配内容");
      return;
    }
    const el = document.getElementById(matchIds[0]);
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const renderHighlight = (text) => {
    if (!query.trim()) return text;
    const lower = text.toLowerCase();
    const q = query.toLowerCase();
    const idx = lower.indexOf(q);
    if (idx === -1) return text;
    const before = text.slice(0, idx);
    const hit = text.slice(idx, idx + q.length);
    const after = text.slice(idx + q.length);
    return (
      <>
        {before}
        <mark className="text-highlight">{hit}</mark>
        {after}
      </>
    );
  };

  const tocItems = useMemo(
    () => contentBlocks.filter((b) => b.type === "h"),
    [contentBlocks]
  );

  const handleBookmark = () => {
    const doc = document.documentElement;
    addBookmark({
      postId: post.id,
      title: post.title,
      scrollTop: doc.scrollTop,
    });
    message.success("已添加书签");
  };

  if (!post) {
    return (
      <Card
        className="themed-card"
        bodyStyle={{ padding: "20px 22px" }}
      >
        <Typography.Title
          level={4}
          style={{ marginTop: 0, color: "var(--text-primary)" }}
        >
          文章不存在
        </Typography.Title>
        <Button
          type="primary"
          onClick={() => navigate("/")}
          style={{
            borderRadius: 10,
            border: 0,
            background: "var(--accent-gradient)",
            boxShadow: "var(--accent-shadow)",
          }}
        >
          回首页
        </Button>
      </Card>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <div
        style={{
          position: "sticky",
          top: 64,
          zIndex: 9,
          height: 4,
          marginBottom: 12,
          background: "rgba(0,0,0,0.06)",
          borderRadius: 999,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${Math.round(progress * 100)}%`,
            background: "var(--accent-gradient)",
            boxShadow: "var(--accent-shadow)",
            transition: "width 0.1s ease",
          }}
        />
      </div>

      <div className="detail-layout">
        <Card
          className="themed-card"
          bodyStyle={{ padding: "22px 26px" }}
          style={{ borderRadius: 18 }}
        >
          <Space direction="vertical" style={{ width: "100%" }} size={10}>
            <Typography.Title
              level={2}
              style={{
                marginBottom: 0,
                letterSpacing: 0.5,
                color: "var(--text-primary)",
              }}
            >
              {post.title}
            </Typography.Title>

            <Space wrap>
              <Typography.Text
                type="secondary"
                style={{ color: "var(--text-secondary)" }}
              >
                {dayjs(post.createdAt).format("YYYY-MM-DD HH:mm")}
              </Typography.Text>
              {post.category ? (
                <Tag
                  style={{
                    borderRadius: 999,
                    border: "1px solid var(--tag-border)",
                    background: "var(--tag-bg)",
                    color: "var(--tag-color)",
                  }}
                >
                  {post.category}
                </Tag>
              ) : null}
              {(post.tags || []).map((t) => (
                <Tag
                  key={t}
                  style={{
                    borderRadius: 999,
                    border: "1px solid var(--tag-border)",
                    background: "var(--tag-bg)",
                    color: "var(--tag-color)",
                  }}
                >
                  {t}
                </Tag>
              ))}
            </Space>

            <Divider
              style={{ margin: "12px 0", borderColor: "rgba(0,0,0,0.06)" }}
            />

            {post.summary ? (
              <Typography.Paragraph
                type="secondary"
                style={{ color: "var(--text-secondary)" }}
              >
                {post.summary}
              </Typography.Paragraph>
            ) : null}

            <div>
              {contentBlocks.map((block) => {
                if (block.type === "h") {
                  return (
                  <Typography.Title
                    key={block.id}
                    id={block.id}
                    level={block.level + 1}
                    style={{
                      marginBottom: 6,
                      color: "var(--text-primary)",
                      scrollMarginTop: 90,
                    }}
                  >
                    {renderHighlight(block.text)}
                  </Typography.Title>
                  );
                }
                if (!block.text.trim()) {
                  return <div key={block.id} style={{ height: 10 }} />;
                }
                return (
                  <Typography.Paragraph
                    key={block.id}
                    id={block.id}
                    style={{
                      marginBottom: 10,
                      color: "var(--text-primary)",
                    }}
                  >
                    {renderHighlight(block.text)}
                  </Typography.Paragraph>
                );
              })}
            </div>

            <Divider
              style={{ margin: "12px 0", borderColor: "rgba(0,0,0,0.06)" }}
            />

            <Space>
              <Button onClick={() => navigate(-1)} style={{ borderRadius: 10 }}>
                返回
              </Button>
              <Button onClick={handleBookmark} style={{ borderRadius: 10 }}>
                添加书签
              </Button>
              {isAuthed ? (
                <Button
                  onClick={() => navigate(`/write/${post.id}`)}
                  style={{ borderRadius: 10 }}
                >
                  编辑
                </Button>
              ) : null}
              {isAuthed ? (
                <Button
                  danger
                  onClick={async () => {
                    await removePost(post.id);
                    message.success("已删除");
                    navigate("/", { replace: true });
                  }}
                  style={{ borderRadius: 10 }}
                >
                  删除
                </Button>
              ) : null}
            </Space>
          </Space>
        </Card>

        <Card className="themed-card detail-toc" bodyStyle={{ padding: 16 }}>
          <Input.Search
            allowClear
            placeholder="搜索正文关键词"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onSearch={handleLocate}
            enterButton="定位"
            style={{ marginBottom: 12 }}
          />
          <Typography.Text
            style={{ color: "var(--text-secondary)", display: "block" }}
          >
            目录 {query ? `· ${matchIds.length} 处匹配` : ""}
          </Typography.Text>
          <Space direction="vertical" size={8} style={{ marginTop: 12 }}>
            {tocItems.length ? (
              tocItems.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  style={{
                    color: "var(--text-primary)",
                    textDecoration: "none",
                    fontSize: 13,
                    paddingLeft: item.level > 1 ? 10 : 0,
                    opacity: item.level > 1 ? 0.8 : 1,
                  }}
                >
                  {item.text}
                </a>
              ))
            ) : (
              <Typography.Text style={{ color: "var(--text-secondary)" }}>
                暂无标题
              </Typography.Text>
            )}
          </Space>
        </Card>
      </div>
    </div>
  );
}
